// src/components/admin/AppBrandingSettingsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { helpContentService } from '../../services/helpContentService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';

// Plain text/URL settings — saved as-is via updateAppSettings().
const TEXT_FIELDS = [
    { key: 'powered_by_text', label: 'Powered By — Label Text', placeholder: 'Powered by:', type: 'text' },
    { key: 'powered_by_company', label: 'Powered By — Company Name', placeholder: 'ICT Technologies', type: 'text' },
    { key: 'terms_url', label: 'Terms of Service URL', placeholder: 'https://ehaazri.com/terms', type: 'url' },
    { key: 'privacy_policy_url', label: 'Privacy Policy URL', placeholder: 'https://ehaazri.com/privacy', type: 'url' }
];

// Image settings — uploaded as base64 in the same PUT request, matching
// api/index.php's updateAppSettings() image branch (setting_type==='image'
// + image_data + image_extension), which writes the resulting
// /uploads/app_settings/... path back into setting_value.
const IMAGE_FIELDS = [
    {
        key: 'app_logo',
        label: 'App Logo',
        description: "Shown on the login/enrollment screen. Falls back to the app's built-in logo until set."
    },
    {
        key: 'splash_logo',
        label: 'Splash Screen Logo',
        description: "Shown on the splash screen while the app loads. Falls back to the app's built-in logo until set."
    }
];

const buildAssetUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${process.env.REACT_APP_API_URL || ''}${path}`;
};

const AppBrandingSettingsManagement = () => {
    const [values, setValues] = useState({});
    // Local-only preview (data URL) for an image picked but not yet saved.
    const [imagePreviews, setImagePreviews] = useState({});
    // key -> { data: base64 (no data: prefix), extension } queued for the
    // next save — only included in the payload for images that actually
    // changed, so re-saving the form doesn't needlessly re-upload logos.
    const [pendingImages, setPendingImages] = useState({});
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => setToast({ show: true, message, type });

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true);
            const response = await helpContentService.getAppSettings();
            const settings = response.settings || [];
            const map = {};
            settings.forEach(setting => {
                map[setting.setting_key] = setting.setting_value;
            });
            setValues(map);
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleTextChange = (key, value) => {
        setValues(prev => ({ ...prev, [key]: value }));
    };

    const handleImageChange = (key, e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            showToast('Invalid file type. Please upload PNG, JPG, SVG, or WEBP', 'error');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            showToast('File size must be less than 2MB', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            setImagePreviews(prev => ({ ...prev, [key]: dataUrl }));

            const base64 = dataUrl.split(',')[1];
            const extension = file.name.split('.').pop() || 'png';
            setPendingImages(prev => ({ ...prev, [key]: { data: base64, extension } }));
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaveLoading(true);
            const settings = [
                ...TEXT_FIELDS.map(field => ({
                    setting_key: field.key,
                    setting_value: values[field.key] || ''
                })),
                ...IMAGE_FIELDS.filter(field => pendingImages[field.key]).map(field => ({
                    setting_key: field.key,
                    setting_type: 'image',
                    image_data: pendingImages[field.key].data,
                    image_extension: pendingImages[field.key].extension
                }))
            ];

            await helpContentService.updateAppSettings(settings);
            showToast('Branding & legal settings updated successfully', 'success');
            setPendingImages({});
            await loadSettings();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            <div style={{ marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                    Branding & Legal
                </h2>
                <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                    App logo, "Powered by" footer text, and the Terms/Privacy links shown on the mobile app's Support screen
                </p>
            </div>

            <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#444', marginTop: 0, marginBottom: '16px' }}>
                    Logos
                </h3>
                {IMAGE_FIELDS.map(field => {
                    const previewSrc = imagePreviews[field.key] || buildAssetUrl(values[field.key]);
                    return (
                        <div key={field.key} style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                                {field.label}
                            </label>
                            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#888' }}>
                                {field.description}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        background: '#fafafa',
                                        flexShrink: 0
                                    }}
                                >
                                    {previewSrc ? (
                                        <img
                                            src={previewSrc}
                                            alt={field.label}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <span style={{ fontSize: '11px', color: '#aaa' }}>No logo</span>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                    onChange={(e) => handleImageChange(field.key, e)}
                                />
                            </div>
                        </div>
                    );
                })}

                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#444', marginTop: '24px', marginBottom: '16px' }}>
                    Footer & Legal
                </h3>
                {TEXT_FIELDS.map(field => (
                    <div key={field.key} style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            {field.label}
                        </label>
                        <input
                            type={field.type}
                            value={values[field.key] || ''}
                            onChange={(e) => handleTextChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                ))}

                <button
                    type="submit"
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: saveLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: saveLoading ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    disabled={saveLoading}
                >
                    {saveLoading && <LoadingSpinner size="small" />}
                    {saveLoading ? 'Saving...' : 'Save Settings'}
                </button>
            </form>
        </div>
    );
};

export default AppBrandingSettingsManagement;
